import React, { useState } from 'react';
import { Cigarette, CheckCircle, Circle } from 'lucide-react';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import ToolPageLayout from '../../components/ToolPageLayout';
import { useLanguage } from '../../contexts/LanguageContext';

const MILESTONE_KEYS = [
  { hours: 20 / 60, labelKey: 'smoking_m1_label', textKey: 'smoking_m1_text' },
  { hours: 12, labelKey: 'smoking_m2_label', textKey: 'smoking_m2_text' },
  { hours: 24 * 2, labelKey: 'smoking_m3_label', textKey: 'smoking_m3_text' },
  { hours: 24 * 21, labelKey: 'smoking_m4_label', textKey: 'smoking_m4_text' },
  { hours: 24 * 30 * 9, labelKey: 'smoking_m5_label', textKey: 'smoking_m5_text' },
  { hours: 24 * 365, labelKey: 'smoking_m6_label', textKey: 'smoking_m6_text' },
  { hours: 24 * 365 * 5, labelKey: 'smoking_m7_label', textKey: 'smoking_m7_text' },
  { hours: 24 * 365 * 10, labelKey: 'smoking_m8_label', textKey: 'smoking_m8_text' },
];

const SmokingSavingsCalculator = () => {
  const { t } = useLanguage();
  const MILESTONES = MILESTONE_KEYS.map((m) => ({ hours: m.hours, label: t(m.labelKey), text: t(m.textKey) }));
  const [perDay, setPerDay] = useState('');
  const [pricePerPack, setPricePerPack] = useState('');
  const [cigsPerPack, setCigsPerPack] = useState('20');
  const [quitDate, setQuitDate] = useState('');

  const n = parseFloat(perDay);
  const price = parseFloat(pricePerPack);
  const perPack = parseFloat(cigsPerPack) || 20;
  const costPerCig = price > 0 ? price / perPack : 0;

  let hoursSince = 0;
  if (quitDate) {
    const d = new Date(quitDate + 'T00:00:00Z');
    const now = new Date();
    hoursSince = Math.max(0, (now - d) / (1000 * 60 * 60));
  }

  const daysSince = hoursSince / 24;
  const savedDzd = n > 0 && costPerCig > 0 ? Math.round(n * costPerCig * daysSince) : null;
  const cigsAvoided = n > 0 ? Math.round(n * daysSince) : null;

  return (
    <ToolPageLayout
      title={t('smoking_title')}
      subtitle={t('smoking_subtitle')}
      specialty="pneumologue"
    >
      <div className="grid sm:grid-cols-2 gap-5 mb-6">
        <div>
          <Label className="mb-1 block">{t('smoking_per_day_label')}</Label>
          <Input type="number" min="1" max="100" value={perDay} onChange={(e) => setPerDay(e.target.value)} placeholder="Ex: 15" className="rounded-xl" />
        </div>
        <div>
          <Label className="mb-1 block">{t('smoking_price_label')}</Label>
          <Input type="number" min="1" value={pricePerPack} onChange={(e) => setPricePerPack(e.target.value)} placeholder="Ex: 250" className="rounded-xl" />
        </div>
        <div>
          <Label className="mb-1 block">{t('smoking_cigs_per_pack_label')}</Label>
          <Input type="number" min="1" max="40" value={cigsPerPack} onChange={(e) => setCigsPerPack(e.target.value)} className="rounded-xl" />
        </div>
        <div>
          <Label className="mb-1 block">{t('smoking_quit_date_label')}</Label>
          <Input type="date" value={quitDate} onChange={(e) => setQuitDate(e.target.value)} className="rounded-xl" />
        </div>
      </div>

      {savedDzd !== null && quitDate && (
        <div className="grid sm:grid-cols-2 gap-4 mb-8">
          <div className="bg-[#F0FDF4] border border-[#10B981]/30 rounded-2xl p-5 text-center">
            <p className="text-xs text-[#065F46] mb-1">{t('smoking_saved_money_label')}</p>
            <p className="text-2xl font-bold text-[#10B981]">{savedDzd.toLocaleString('fr-FR')} DZD</p>
          </div>
          <div className="bg-[#F8FAFC] border border-[#E2E8F0] rounded-2xl p-5 text-center">
            <p className="text-xs text-[#64748B] mb-1">{t('smoking_avoided_cigs_label')}</p>
            <p className="text-2xl font-bold text-[#0F172A]">{cigsAvoided.toLocaleString('fr-FR')}</p>
          </div>
        </div>
      )}

      {quitDate && (
        <div>
          <h3 className="font-semibold text-[#0F172A] mb-3 flex items-center gap-2">
            <Cigarette className="w-5 h-5 text-[#0596DE]" /> {t('smoking_benefits_heading')}
          </h3>
          <div className="space-y-2">
            {MILESTONES.map((m, i) => {
              const done = hoursSince >= m.hours;
              return (
                <div key={i} className={`rounded-xl p-4 border flex gap-3 ${done ? 'bg-[#F0FDF4] border-[#10B981]/30' : 'bg-[#F8FAFC] border-[#E2E8F0]'}`}>
                  {done ? <CheckCircle className="w-5 h-5 text-[#10B981] shrink-0 mt-0.5" /> : <Circle className="w-5 h-5 text-[#94A3B8] shrink-0 mt-0.5" />}
                  <div>
                    <p className="font-semibold text-[#0F172A] text-sm">{m.label}</p>
                    <p className="text-sm text-[#64748B]">{m.text}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      <p className="mt-6 text-sm text-[#64748B]">
        {t('smoking_disclaimer')}
      </p>
    </ToolPageLayout>
  );
};

export default SmokingSavingsCalculator;
